import { EChartsOption } from 'echarts';
import { MixedChartConfig } from '../models/chart-types';
import {
  getTitleConfig,
  getAxisLabelConfig,
  getSplitLineConfig,
  getGridConfig,
  getLegendConfig,
  CHART_DEFAULTS,
} from '../constants/chart-theme.constants';

/**
 * Build ECharts options for mixed chart (bar + line)
 */
export function buildMixedChartOptions(config: MixedChartConfig): EChartsOption {
  const series: any[] = [];

  // Add bar series
  config.barSeries.forEach((barSeries) => {
    series.push({
      name: barSeries.name,
      type: 'bar',
      data: barSeries.data,
      color: barSeries.color,
      stack: barSeries.stack,
      barWidth: barSeries.barWidth,
      barGap: barSeries.barGap,
      itemStyle: barSeries.itemStyle,
      emphasis: {
        focus: CHART_DEFAULTS.emphasis.focus,
      },
    });
  });

  // Add line series
  config.lineSeries.forEach((lineSeries) => {
    series.push({
      name: lineSeries.name,
      type: 'line',
      data: lineSeries.data,
      smooth: lineSeries.smooth !== undefined ? lineSeries.smooth : true,
      showSymbol: lineSeries.showSymbol !== false,
      color: lineSeries.color,
      lineStyle: lineSeries.lineStyle || {
        width: 2,
      },
      areaStyle: lineSeries.areaStyle,
      emphasis: lineSeries.emphasis || {
        focus: CHART_DEFAULTS.emphasis.focus,
      },
      z: 10, // Ensure lines appear on top of bars
    });
  });

  const options: EChartsOption = {
    title: getTitleConfig(config.title, config.subtitle),

    tooltip: {
      trigger: config.tooltip?.trigger || 'axis',
      axisPointer: {
        type: 'cross',
        crossStyle: {
          color: '#999',
        },
      },
      ...config.tooltip,
    },

    legend: getLegendConfig(config.legend),

    grid: config.grid || getGridConfig(!!config.title),

    xAxis: {
      type: config.xAxis.type || 'category',
      data: config.xAxis.data,
      name: config.xAxis.name,
      axisLabel: getAxisLabelConfig(config.xAxis.axisLabel),
      axisLine: config.xAxis.axisLine,
      axisTick: config.xAxis.axisTick,
      splitLine: getSplitLineConfig(config.xAxis.splitLine),
    },

    yAxis: {
      type: config.yAxis.type || 'value',
      name: config.yAxis.name,
      axisLabel: getAxisLabelConfig(config.yAxis.axisLabel),
      axisLine: config.yAxis.axisLine,
      axisTick: config.yAxis.axisTick,
      splitLine: getSplitLineConfig(config.yAxis.splitLine),
    },

    series,
  };

  // Merge with custom options if provided
  return config.customOptions 
    ? { ...options, ...config.customOptions }
    : options;
}

